import Button from '@digitalshared/button';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { GoogleReCaptcha, GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { useForm } from 'react-hook-form';
import Modal from 'react-modal';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { z } from 'zod';
import { ApiService } from '../../js/ApiService';
import JDFNLBrand from '../../styles/themes/status/assets/JD_FNL_Brand.svg';
import ModalSkeleton from '../ModalSkeleton';
import InputField from '../util/InputField/InputField';
import getRISKX from '../util/Riskified/getRISKX';
import useClient from '../util/useClient/useClient';
import useCooldown from '../util/useCooldown/useCooldown';
import useFeatureFlag from '../util/useFeatureFlag/useFeatureFlag';
import useSiteKey from '../util/useSiteKey/useSiteKey';

const schema = z.object({
  username: z.string().email({ message: 'Please enter a valid email address' }),
  password: z.string().min(1, { message: 'Password is required.' }).max(25, {
    message: 'Password must be at most 25 characters.'
  })
});

export default function Login() {
  const {
    register,
    errors,
    handleSubmit,
    setValue,
    reset,
    watch,
    trigger,
    // isValid is read so react-hook-form subscribes the component to validation updates,
    // which keeps `errors` fresh between renders (used by the step-1 Continue gate).
    // eslint-disable-next-line no-unused-vars
    formState: { isSubmitting, isValid }
  } = useForm({ resolver: zodResolver(schema), mode: 'onChange' });
  const search = useLocation().search;
  const urlParams = useMemo(() => new URLSearchParams(search), [search]);
  const [BEError, setBEError] = useState(urlParams.get('error') || false);
  const [BEErrorMsg, setBEErrorMsg] = useState(
    urlParams.get('displayError') || 'Something happened when logging in'
  );
  const [passInputType, setPassInputType] = useState('password');
  const [step, setStep] = useState('email');
  // Only show the password "required" error after the user attempts to sign in
  // from the password step — never from stale errors/submits made on the email
  // step (e.g. pressing Enter there implicitly submits the whole form).
  const [passwordSubmitAttempted, setPasswordSubmitAttempted] = useState(false);
  const username = watch('username');
  const { validateChallengeToken } = ApiService;
  const tokenNotFound = 'Challenge%20token%20not%20found';
  const siteKey = useSiteKey();
  const client = useClient();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { isEnabled: isMagicLinkFlagEnabled, flagValue } = useFeatureFlag('magicLink', 'signin');
  const emailLower = username?.toLowerCase();
  const trimmedFlagValue = typeof flagValue === 'string' ? flagValue.trim() : '';
  const hasCustomSuffix = !!(
    trimmedFlagValue && !['true', 'false'].includes(trimmedFlagValue.toLowerCase())
  );
  const isCustomTester = !!(
    hasCustomSuffix &&
    emailLower &&
    emailLower.endsWith(trimmedFlagValue.toLowerCase())
  );
  const isMagicLinkEnabled = isMagicLinkFlagEnabled || isCustomTester;
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const { cooldown, startCooldown, resetCooldown } = useCooldown(0);
  const history = useHistory();
  const errorMessage = 'Something went wrong. Please try again.';

  const modalStyle = {
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)'
    },
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      padding: '50px, 25px',
      transform: 'translate(-50%, -50%)',
      overflow: 'hidden'
    }
  };

  useEffect(() => {
    document.title = `STATUS Sign In | ${client.startsWith('finl') ? 'Finish Line' : 'JD Sports'}`;
    const params = {
      error: urlParams.get('error'),
      displayError: urlParams.get('displayError')
    };
    if (
      (params.error && params.displayError) ||
      window?.location?.pathname?.includes(tokenNotFound)
    ) {
      reset();
      setBEError(true);
      setBEErrorMsg(params.displayError);
      setLoading(false);
    }
    async function handleRISKX() {
      const RISKX = await getRISKX();
      RISKX.go('/login');
    }

    handleRISKX();
  }, [urlParams, client, reset]);

  const handleMagicLinkClick = async () => {
    try {
      if (magicLinkLoading || cooldown > 0) return;
      setMagicLinkLoading(true);
      setBEError(false);

      if (!username) {
        setBEError(true);
        setBEErrorMsg('Email address is required.');
        setMagicLinkLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append('email', username);
      formData.append('context', 'WEB');
      formData.append('client_id', client);

      const response = await ApiService.requestMagicLink(formData);

      if (response.status === 200) {
        history.push('/checkYourInbox', {
          email: username,
          isMagicLink: true,
          magicLinkPayload: {
            email: username,
            context: 'WEB',
            client_id: client
          }
        });
      } else if (response.status === 429) {
        setBEError(true);
        setBEErrorMsg('Too many magic link requests. Please try again later.');
        startCooldown(30);
      } else {
        setBEError(true);
        setBEErrorMsg('Failed to send magic link. Please try again.');
      }
    } catch (error) {
      console.error('Error requesting magic link', error);
      setBEError(true);
      setBEErrorMsg('Something went wrong. Please try again.');
    } finally {
      setMagicLinkLoading(false);
    }
  };

  const onSubmit = async (_, event) => {
    try {
      setLoading(true);
      event?.preventDefault();
      await event.target.submit();
    } catch (error) {
      console.error('Error submitting form', error);
      setLoading(false);
    }
  };

  // Show the password error only once the user activates Sign In — either by
  // clicking it or by pressing Enter, which the browser turns into a click on
  // the submit button. Deriving this from the form's invalid-submit callback
  // instead would also catch the phantom submit described on the buttons below.
  const handleSignInClick = () => {
    setPasswordSubmitAttempted(true);
  };

  const handleContinue = async () => {
    const valid = await trigger('username');
    if (valid) {
      setBEError(false);
      setPasswordSubmitAttempted(false);
      setStep('password');
    }
  };

  const handleChangeEmail = () => {
    setValue('password', '');
    setPassInputType('password');
    setPasswordSubmitAttempted(false);
    setBEError(false);
    resetCooldown();
    setStep('email');
  };

  const showPassword = () => {
    if (passInputType === 'password') {
      setPassInputType('text');
    } else {
      setPassInputType('password');
    }
  };

  function handleVerify(token) {
    setValue('recaptchaToken', token);
  }

  useLayoutEffect(() => {
    const widgetToken = urlParams.get('widgetToken');
    const challengeAccessToken = urlParams.get('challengeAccessToken');
    const clientId = urlParams.get('clientId') || urlParams.get('client_id');
    const redirectUri = urlParams.get('redirectUri') || urlParams.get('redirect_uri');
    const codeChallenge = urlParams.get('codeChallenge') || urlParams.get('code_challenge');
    const state = urlParams.get('state');
    const otherParams = { clientId, redirectUri, codeChallenge, state };

    if (widgetToken && challengeAccessToken) {
      setLoading(true);
      setOpen(true);
      async function riskifiedMfaWidget() {
        if (window?.Riskified) {
          const callback = async (challengeAccessToken, otherParams) => {
            try {
              const response = await validateChallengeToken(challengeAccessToken, otherParams);
              const json = await response.json();
              const { status } = response;
              const { result } = json;
              if (window && status === 200 && result) {
                window.location.href = result;
              } else {
                setBEError(true);
                setBEErrorMsg(errorMessage);
              }
              setOpen(false);
            } catch (error) {
              console.error('Error validating challenge token', error);
              setBEError(true);
              setBEErrorMsg(errorMessage);
            }
          };
          const onVerifiedCallback = callback.bind(null, challengeAccessToken, otherParams);
          const widget = window.Riskified.renderMfaWidget;
          await widget(widgetToken, onVerifiedCallback);
          setTimeout(() => {
            setLoading(false);
          }, 2000);
        } else {
          console.error('Widget unavailable.');
        }
      }

      setTimeout(async () => {
        await riskifiedMfaWidget();
      }, 4000);
    }
  }, [urlParams, validateChallengeToken]);

  const continueDisabled = loading;

  return (
    <>
      <div className="body">
        <div className="bg-white">
          <div className="d-flex flex-column align-items-center justify-content-center">
            <h1 className="h1 mb-2 text-center" style={{ marginTop: '28px' }}>
              SIGN IN
            </h1>
            <p className="text-center text-muted-status mb-4">
              Gain Access. Get Cash. Boost Your STATUS.
            </p>
          </div>
          <div className="form">
            <form
              id="loginForm"
              method="POST"
              action="/account/login"
              onSubmit={
                step === 'email'
                  ? event => {
                      // Enter in the email field implicitly submits the form;
                      // treat it as Continue instead of a sign-in attempt.
                      event.preventDefault();
                      handleContinue();
                    }
                  : handleSubmit(onSubmit)
              }
            >
              <div className="mb-2" style={{ display: step === 'email' ? 'block' : 'none' }}>
                <InputField
                  name="username"
                  type="email"
                  autoComplete="username"
                  placeholder="Email Address"
                  aria-describedby="Email"
                  aria-labelledby="username"
                  data-testid="Email"
                  ref={register()}
                  error={step === 'email' ? errors.username : undefined}
                />
              </div>

              {step === 'password' && (
                <div className="mb-2 text-center" data-testid="EmailDisplay">
                  <strong>{username}</strong>
                  <span className="text-muted-status" style={{ margin: '0 8px' }}>
                    |
                  </span>
                  <button
                    type="button"
                    onClick={handleChangeEmail}
                    className="link"
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      textDecoration: 'underline',
                      cursor: 'pointer'
                    }}
                    data-testid="ChangeEmail"
                  >
                    Change
                  </button>
                </div>
              )}

              {step === 'password' && (
                <div className="mb-2">
                  <InputField
                    name="password"
                    type={passInputType}
                    maxLength="25"
                    placeholder="Password"
                    autoComplete="current-password"
                    data-testid="Password"
                    aria-describedby="Password"
                    aria-labelledby="password"
                    ref={register()}
                    error={passwordSubmitAttempted ? errors.password : undefined}
                  >
                    <button type="button" onClick={showPassword} className="show-pass">
                      {passInputType === 'password' ? 'Show' : 'Hide'}
                    </button>
                  </InputField>
                </div>
              )}

              {step === 'password' && (
                <Link to="/resetPassword" className="link text-left">
                  Forgot your password?
                </Link>
              )}

              <div className="text-center py-2">
                {/*
                  The keys keep these two buttons as separate DOM nodes. Without
                  them React reuses one <button> across the step change and just
                  patches type="button" -> type="submit"; because handleContinue
                  awaits trigger(), that re-render flushes before the browser
                  runs the click's default action, so the click that advanced the
                  step lands on a submit button and submits the empty password.
                */}
                {step === 'email' ? (
                  <Button
                    key="continue"
                    category="primary"
                    disabled={continueDisabled}
                    type="button"
                    onClick={handleContinue}
                    className="btn btn-primary btn-block"
                    data-testid="Continue"
                  >
                    Continue
                  </Button>
                ) : (
                  <Button
                    key="signIn"
                    category="primary"
                    disabled={isSubmitting || loading}
                    loading={loading}
                    type="submit"
                    onClick={handleSignInClick}
                    className="btn btn-primary btn-block"
                  >
                    {loading ? 'Signing in' : 'Sign In'}
                  </Button>
                )}
              </div>
              <div className="py-1 mb-4" style={{ display: step === 'email' ? 'block' : 'none' }}>
                <InputField name="recaptchaToken" type="hidden" ref={register()} />
                <span className="recaptchaMsg">
                  This site is protected by reCAPTCHA and the Google{' '}
                  <a
                    href="https://policies.google.com/privacy"
                    className="recaptchaMsg"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://policies.google.com/terms"
                    className="recaptchaMsg"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Service
                  </a>{' '}
                  apply.
                </span>
              </div>

              {step === 'password' && isMagicLinkEnabled && (
                <>
                  <div className="d-flex align-items-center w-100 my-2">
                    <div className="flex-grow-1 border-top"></div>
                    <span className="mx-2 body2 text-muted text-uppercase">Or</span>
                    <div className="flex-grow-1 border-top"></div>
                  </div>
                  <div className="text-center py-4">
                    <Button
                      category="secondary"
                      className="btn btn-block btn-secondary-rounded"
                      type="button"
                      data-testid="EmailingLink"
                      onClick={handleMagicLinkClick}
                      disabled={magicLinkLoading || loading || cooldown > 0}
                      loading={magicLinkLoading}
                    >
                      {magicLinkLoading
                        ? 'Sending Link'
                        : cooldown > 0
                          ? `Resend Link (${cooldown}s)`
                          : 'Email Me A Sign-In Link'}
                    </Button>
                  </div>
                </>
              )}

              {step === 'password' && (
                <div className="text-center pt-2 pb-4" data-testid="SignUpInStore">
                  <span className="text-muted-status">Sign up in store?</span>
                  <span className="text-muted-status" style={{ margin: '0 8px' }}>
                    |
                  </span>
                  <Link to="/completeRegistration" className="link" data-testid="VerifyAccount">
                    Verify Your Account
                  </Link>
                </div>
              )}
            </form>
            {BEError && <div className="text-danger text-center pb-4">{BEErrorMsg}</div>}
          </div>
        </div>
        {step === 'email' && (
          <>
            <hr className="fl-hr"></hr>
            <div className="formFooter text-center">
              <h2 className="h1 mb-3 text-center" style={{ marginTop: '28px' }}>
                DON&apos;T HAVE AN ACCOUNT?
              </h2>
              <div className="text-center mb-3" style={{ lineHeight: '20px' }}>
                <strong>Sign Up, Get An Exclusive $10 Welcome Reward.</strong>
                <div className="text-muted-status">Create an account today and start earning.</div>
                <div className="text-muted-status">Earn 10 points for every $1 you spend.</div>
              </div>
              <Link to="/register">
                <button className="btn btn-secondary btn-block" style={{ marginBottom: '16px' }}>
                  Sign Up
                </button>
              </Link>
              <div className="text-center mb-3" style={{ lineHeight: '20px' }}>
                <a
                  href={
                    client.startsWith('finl')
                      ? 'https://www.finishline.com/store/status'
                      : 'https://www.jdsports.com/status'
                  }
                  className="link"
                >
                  Learn More
                </a>
              </div>
            </div>
          </>
        )}
        <GoogleReCaptchaProvider reCaptchaKey={siteKey} useEnterprise="true">
          <GoogleReCaptcha onVerify={handleVerify} />
        </GoogleReCaptchaProvider>
      </div>
      <Modal
        isOpen={open}
        onRequestClose={() => {
          reset();
          setOpen(false);
          setLoading(false);
          history.push('/login');
        }}
        style={modalStyle}
      >
        <div className="riskifiedModalContainer">
          <div className="riskifiedModalLogo">
            <img className="family-logo" src={JDFNLBrand} alt="JD FNL Brand logo" />
          </div>

          {loading && <ModalSkeleton />}
          <div style={{ width: '100%', height: '100%' }} id="mfa-widget"></div>
        </div>
      </Modal>
    </>
  );
}
Modal.setAppElement('body');
